import { GameStatues, GameStatusFlow, GameTeams, IGame } from "@grassrootly/api/entities";
import { UpdateGameStatusPayload } from "@grassrootly/api/gateways";
import withErrorCheck from "../CheckError";
import server from "../Server";
import { GameType } from "../../../../../src/screens/new-game/NewGame.types";

const validateStatusChange = (game: IGame, newStatus: GameStatues): { valid: boolean; message?: string } => {
  const availableStatuses = GameStatusFlow[game.status];
  if (availableStatuses.indexOf(newStatus) === -1) {
    return { valid: false, message: `Game status can not be changed from ${game.status} into ${newStatus}` };
  }

  /**
   * Note [UP]: One special case when we need business validation
   * Game status changing to FINISHED requires for the game to has result and teams configured
   */
  if (newStatus === GameStatues.FINISHED) {
    if (game.game_type === GameType.COMPETITIVE) {
      const attendees = game.attendees.filter((x) => x.attended).length;
      if (attendees < 3) {
        return { valid: false, message: `At least 3 players must attend the game to finish it` };
      }
      return {
        valid: true,
      };
    }
    const hostTeam = game.attendees.filter((x) => x.team === GameTeams.hosts.key);
    const visitorTeam = game.attendees.filter((x) => x.team === GameTeams.visitors.key);
    // Currently we consider teams configured if there is at least process.env.EXPO_PUBLIC_GAME_TEAM_MIN_LIMIT) players in the team (Igramo malice 3 na 3 :) )
    const minPlayersPerTeam = Number(process.env.EXPO_PUBLIC_GAME_TEAM_MIN_LIMIT);
    if (hostTeam.length < minPlayersPerTeam || visitorTeam.length < minPlayersPerTeam) {
      return {
        valid: false,
        message: `Teams are not properly configured. Each team needs to have at least ${minPlayersPerTeam} players`,
      };
    }

    if ((!game.host_score && game.host_score !== 0) || (!game.visitor_score && game.visitor_score !== 0)) {
      return { valid: false, message: `Game score must be completed before status change` };
    }
  }

  return { valid: true };
};

export const UpdateGameStatus = async (
  payload: UpdateGameStatusPayload
): Promise<Omit<IGame, "group" | "attendees">> => {
  const validationRes = validateStatusChange(payload.game, payload.status);

  if (!validationRes.valid) {
    throw new Error(validationRes.message || `Game status can not be changed into ${payload.status}`);
  }

  return withErrorCheck(
    await server
      .from("Games")
      .update({
        status: payload.status,
      })
      .eq("id", payload.game.id)
      .select()
  )[0] as Omit<IGame, "group" | "attendees">;
};
